// Read-only local trace observer for exactly one pinned SMA-B v2.0.0
// first-deployment fallback-EOA UserOperation. It is deliberately not a
// general EVM verifier, receipt producer, settlement mechanism, or authority.
import {
  decodeEventLog,
  decodeFunctionData,
  decodeFunctionResult,
  encodeAbiParameters,
  encodeEventTopics,
  encodeFunctionData,
  encodePacked,
  getAddress,
  getContractAddress,
  hashMessage,
  isAddressEqual,
  keccak256,
  parseAbi,
  stringToHex,
  verifyMessage,
  type Address,
  type Hex,
} from 'viem';
import { entryPoint07Abi, getUserOperationHash, type UserOperation } from 'viem/account-abstraction';
import type { decodeAndValidateNativeV2Trade } from './nativeRouterTradeDecoder.js';
import { networkBuild } from '@hoodmorning/config';

const CHAIN_ID = 4663;
const SELECTED_ACCOUNT_CHAIN_ID = networkBuild.chainId;
const ENTRY_POINT = '0x0000000071727de22e5e9d8baf0edac6f37da032';
const FACTORY = '0x00000000000017c61b5bee81050ec8efc9c6fecd';
const IMPLEMENTATION = '0x000000000000c5a9089039570dd36455b5c07383';
const ENTRY_POINT_CODE_HASH = '0x8db5ff695839d655407cc8490bb7a5d82337a86a6b39c3f0258aa6c3b582fc58';
const FACTORY_CODE_HASH = '0xd797dac2d304de879abba3ed87b60f9f141b2da869c3ce24c037feb5c5d60342';
const IMPLEMENTATION_CODE_HASH = '0x3612f3fa5ac5e89b87b284024977e6082441e30ff05f9f8497cd280131a27e16';
const IMPLEMENTATION_SLOT = '0x360894a13ba1a3210667c828492db98dca3e2076cc3735a920a3ca505d382bbc';
const FALLBACK_SIGNER_SLOT = '0x5b9dc9aa943f8fa2653ceceda5e3798f0686455280432166ba472eca0bc17a32';
const MAX_TRACE_BYTES = 2 * 1024 * 1024;
const MAX_TRACE_NODES = 20_000;
const MAX_TRACE_FRAMES = 2_048;
const MAX_DEPTH = 64;
const MAX_CALLDATA_BYTES = 256 * 1024;
const MAX_ACCOUNTS = 128;
const MAX_STORAGE_SLOTS = 512;
const encoder = new TextEncoder();

// getProxyBytecodeWithImmutableArgs in the pinned SDK 5.2.0 implementation.
const SMA_B_RUNTIME = '363d3d373d3d363d7f360894a13ba1a3210667c828492db98dca3e2076cc3735a920a3ca505d382bbc545af43d6000803e6038573d6000fd5b3d6000f3';
const SMA_B_INIT_PREFIX = '6100513d8160233d3973';
const SMA_B_INIT_SUFFIX = '60095155f3363d3d373d3d363d7f360894a13ba1a3210667c828492db98dca3e2076cc3735a920a3ca505d382bbc545af43d6000803e6038573d6000fd5b3d6000f3';

const factoryAbi = parseAbi(['function createSemiModularAccount(address owner,uint256 salt) returns (address)']);
const validateAbi = parseAbi(['function validateUserOp((address sender,uint256 nonce,bytes initCode,bytes callData,bytes32 accountGasLimits,uint256 preVerificationGas,bytes32 gasFees,bytes paymasterAndData,bytes signature) userOp,bytes32 userOpHash,uint256 missingAccountFunds) returns (uint256 validationData)']);
const executionAbi = parseAbi([
  'function execute(address target,uint256 value,bytes data) returns (bytes)',
  'function executeBatch((address target,uint256 value,bytes data)[] calls) returns (bytes[])',
]);

type PlainRecord = Record<string, unknown>;

export type SmaBFirstUserOpValidationTraceLocal = (
  method: 'eth_chainId' | 'debug_traceTransaction',
  params: unknown[],
  timeoutMs: number,
) => Promise<unknown>;

export interface SmaBFirstUserOpValidationTraceClient {
  getChainId(): Promise<unknown>;
  getTransaction(args: { hash: Hex }): Promise<unknown>;
  getTransactionReceipt(args: { hash: Hex }): Promise<unknown>;
  getBlock(args: { blockNumber: bigint }): Promise<unknown>;
}

export interface SmaBFirstUserOpValidationTraceExpected {
  vault: Address;
  owner: Address;
  userOperationHash: Hex;
  nonceLane: bigint;
  factory: Address;
  implementation: Address;
  salt: bigint;
  callDataHash: Hex;
}

export interface SmaBFirstUserOpValidationTraceInput {
  local: SmaBFirstUserOpValidationTraceLocal;
  client: SmaBFirstUserOpValidationTraceClient;
  transactionHash: Hex;
  expected: SmaBFirstUserOpValidationTraceExpected;
}

export interface SmaBFirstUserOpValidationTraceEvidence<ChainId extends 4663 | 46630 = 4663> {
  readonly schemaVersion: 1;
  readonly scope: 'local-sma-b-v2.0.0-first-deployment-fallback-eoa-validation-trace';
  readonly chainId: ChainId;
  readonly source: Readonly<{
    transactionHash: Hex;
    blockHash: Hex;
    blockNumber: string;
    transactionIndex: number;
    entryPoint: Address;
    inputHash: Hex;
    userOperationHash: Hex;
    nonce: string;
    traceCommitments: Readonly<{ fullPrestate: Hex; diff: Hex; call: Hex }>;
    traceProfile: Readonly<{ prestate: 'prestateTracer'; diff: 'prestateTracer.diffMode'; call: 'callTracer.withLog' }>;
    callTrace: RedactedCall;
    traceNotCryptographicProof: true;
  }>;
  readonly validation: Readonly<{
    kind: 'sma-b-v2.0.0-counterfactual-fallback-eoa-observed';
    vault: Address;
    owner: Address;
    nonceLane: string;
    lookupKey: '0';
    globalValidation: true;
    deferredAction: false;
    directCallValidation: false;
    signatureFormat: 'ff00-eoa-65-byte-redacted';
    signatureCommitment: Hex;
    factory: Readonly<{ address: Address; inputHash: Hex; create2InitCodeHash: Hex }>;
    implementation: Readonly<{ address: Address; runtimeHash: Hex; implementationSlot: typeof IMPLEMENTATION_SLOT }>;
    validateUserOp: Readonly<{ inputHash: Hex; outputHash: Hex; validationData: '0' }>;
    executionInputHash: Hex;
  }>;
  readonly traceBounds: Readonly<{
    maxBytesPerTrace: number;
    maxAccounts: number;
    maxStorageSlotsPerAccount: number;
    maxFrames: number;
    maxDepth: number;
    observedFrames: number;
  }>;
  readonly limitations: readonly string[];
  readonly executionAuthorized: false;
  readonly resubmissionAuthorized: false;
  readonly movementPublicationAuthorized: false;
  readonly accountingEligible: false;
  readonly feeSettlementAuthorized: false;
  readonly pnlPublicationAuthorized: false;
  readonly referralSettlementAuthorized: false;
  readonly pointsPublicationAuthorized: false;
  readonly executionTimeOwnerVerified: false;
}

interface SourceIdentity {
  transactionHash: Hex;
  blockHash: Hex;
  blockNumber: string;
  transactionIndex: number;
  entryPoint: Address;
  inputHash: Hex;
}

interface CanonicalPacked {
  sender: Address;
  nonce: bigint;
  initCode: Hex;
  callData: Hex;
  accountGasLimits: Hex;
  preVerificationGas: bigint;
  gasFees: Hex;
  paymasterAndData: Hex;
  signature: Hex;
}

interface ObservedOperation {
  packed: CanonicalPacked;
  factory: Address | undefined;
  factoryData: Hex | undefined;
  userOperationHash: Hex;
  signatureCommitment: Hex;
  success: boolean;
}

interface TraceFrame {
  index: number;
  parent: number | null;
  type: string;
  from: Address;
  to: Address;
  input: Hex;
  output: Hex;
  error: string | null;
  gas: string | null;
  gasUsed: string | null;
  value: string | null;
  children: number[];
}

interface ParsedCallTree {
  nodes: TraceFrame[];
  rootIndex: number;
  frames: number;
  redacted: RedactedCall;
}

interface TraceSemantics {
  factoryInputHash: Hex;
  create2InitCodeHash: Hex;
  validationInputHash: Hex;
  validationOutputHash: Hex;
  executionInputHash: Hex;
}

export interface RedactedCall {
  type: string;
  from: Address;
  to: Address;
  gas: string | null;
  gasUsed: string | null;
  inputSelector: Hex | null;
  inputHash: Hex;
  outputHash: Hex;
  reverted: boolean;
  calls: RedactedCall[];
}

const falseAuthorities = Object.freeze({
  executionAuthorized: false as const,
  resubmissionAuthorized: false as const,
  movementPublicationAuthorized: false as const,
  accountingEligible: false as const,
  feeSettlementAuthorized: false as const,
  pnlPublicationAuthorized: false as const,
  referralSettlementAuthorized: false as const,
  pointsPublicationAuthorized: false as const,
  executionTimeOwnerVerified: false as const,
});

function fail(message: string): never { throw new Error(`sma-b-first-validation-trace: ${message}`); }

function parsed<T>(label: string, read: () => T): T {
  try { return read(); }
  catch { return fail(`invalid ${label}`); }
}

async function parsedAsync<T>(label: string, read: () => Promise<T>): Promise<T> {
  try { return await read(); }
  catch { return fail(`invalid ${label}`); }
}

function plain(value: unknown, label: string): PlainRecord {
  if (!value || typeof value !== 'object' || Array.isArray(value) || Object.getPrototypeOf(value) !== Object.prototype) fail(`${label} must be an object`);
  return value as PlainRecord;
}

function hex(value: unknown, label: string, bytes?: number): Hex {
  if (typeof value !== 'string' || !/^0x[0-9a-fA-F]*$/.test(value) || value.length % 2 !== 0) fail(`invalid ${label}`);
  if (bytes !== undefined && value.length !== 2 + bytes * 2) fail(`invalid ${label} length`);
  return value.toLowerCase() as Hex;
}

function hash(value: unknown, label: string): Hex { return hex(value, label, 32); }

function address(value: unknown, label: string): Address {
  try { return getAddress(hex(value, label, 20)); }
  catch { return fail(`invalid ${label}`); }
}

function uint(value: unknown, label: string): bigint {
  if (typeof value === 'bigint') {
    if (value < 0n || value >= 1n << 256n) fail(`invalid ${label}`);
    return value;
  }
  if (typeof value !== 'string' || !/^0x[0-9a-fA-F]+$/.test(value) || value.length > 66) fail(`invalid ${label}`);
  return BigInt(value);
}

function traceNonce(value: unknown, label: string): bigint {
  if (typeof value === 'number') {
    if (!Number.isSafeInteger(value) || value < 0) fail(`invalid ${label}`);
    return BigInt(value);
  }
  return uint(value, label);
}

function index(value: unknown, label: string): number {
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0) fail(`invalid ${label}`);
  return value;
}

function byteLength(value: string): number { return encoder.encode(value).byteLength; }

function canonical(value: unknown): string {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return JSON.stringify(value);
  if (typeof value === 'number') {
    if (!Number.isSafeInteger(value)) fail('trace contains non-safe number');
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
  const object = plain(value, 'trace value');
  return `{${Object.keys(object).sort().map(key => `${JSON.stringify(key)}:${canonical(object[key])}`).join(',')}}`;
}

function boundRaw(value: unknown, label: string): void {
  const seen = new WeakSet<object>();
  let nodes = 0;
  let bytes = 0;
  function visit(item: unknown, depth: number): void {
    if (++nodes > MAX_TRACE_NODES || depth > MAX_DEPTH) fail(`${label} node/depth bound exceeded`);
    if (item === null || typeof item === 'boolean') return;
    if (typeof item === 'string') {
      bytes += byteLength(item);
      if (bytes > MAX_TRACE_BYTES) fail(`${label} exceeds byte bound`);
      return;
    }
    if (typeof item === 'number') {
      if (!Number.isSafeInteger(item)) fail(`${label} contains non-safe number`);
      return;
    }
    if (!item || typeof item !== 'object') fail(`${label} contains unsupported value`);
    if (seen.has(item)) fail(`${label} is cyclic`);
    seen.add(item);
    if (Array.isArray(item)) {
      for (const child of item) visit(child, depth + 1);
      return;
    }
    const object = plain(item, label);
    for (const [key, child] of Object.entries(object)) {
      bytes += byteLength(key);
      if (bytes > MAX_TRACE_BYTES) fail(`${label} exceeds byte bound`);
      visit(child, depth + 1);
    }
  }
  visit(value, 0);
}

function commitment(value: unknown, label: string): Hex {
  boundRaw(value, label);
  const encoded = canonical(value);
  if (byteLength(encoded) > MAX_TRACE_BYTES) fail(`${label} exceeds byte bound`);
  return keccak256(stringToHex(encoded));
}

function normalizedSlot(value: unknown, label: string): Hex {
  const raw = hex(value, label);
  if (raw.length > 66) fail(`oversized ${label}`);
  return `0x${raw.slice(2).padStart(64, '0')}` as Hex;
}

function addressMap(raw: unknown, label: string): Map<string, PlainRecord> {
  const object = plain(raw, label);
  const result = new Map<string, PlainRecord>();
  const entries = Object.entries(object);
  if (entries.length > MAX_ACCOUNTS) fail(`${label} account bound exceeded`);
  for (const [key, value] of entries) {
    const normalized = address(key, `${label} account`).toLowerCase();
    if (result.has(normalized)) fail(`${label} has case-aliased account`);
    result.set(normalized, plain(value, `${label} account state`));
  }
  return result;
}

function storageMap(account: PlainRecord, label: string, optional = false): Map<Hex, bigint> {
  if (!Object.hasOwn(account, 'storage')) {
    if (optional) return new Map();
    return fail(`missing ${label} storage`);
  }
  const object = plain(account.storage, `${label} storage`);
  const result = new Map<Hex, bigint>();
  const entries = Object.entries(object);
  if (entries.length > MAX_STORAGE_SLOTS) fail(`${label} storage bound exceeded`);
  for (const [key, value] of entries) {
    const slot = normalizedSlot(key, `${label} storage slot`);
    if (result.has(slot)) fail(`${label} has case-aliased storage slot`);
    result.set(slot, uint(value, `${label} storage value`));
  }
  return result;
}

function prestateCode(prestate: Map<string, PlainRecord>, target: string, codeHash: Hex, label: string): void {
  const account = prestate.get(target.toLowerCase());
  if (!account) fail(`missing prestate ${label}`);
  const code = hex(account.code, `prestate ${label} code`);
  if (code === '0x' || keccak256(code).toLowerCase() !== codeHash) fail(`prestate ${label} pin mismatch`);
}

// A counterfactual address may hold ETH before deployment. Balance is therefore
// deliberately unconstrained; code, nonce, and storage must still be empty.
function emptyCounterfactual(account: PlainRecord | undefined, label: string): void {
  if (!account) return;
  if (Object.hasOwn(account, 'code') && hex(account.code, `${label} code`) !== '0x') fail(`${label} has pre-existing code`);
  if (Object.hasOwn(account, 'nonce') && traceNonce(account.nonce, `${label} nonce`) !== 0n) fail(`${label} has pre-existing nonce`);
  if ([...storageMap(account, label, true).values()].some(value => value !== 0n)) fail(`${label} has pre-existing storage`);
}

function postDeployment(poststate: Map<string, PlainRecord>, vault: Address, owner: Address): { runtimeHash: Hex; implementationSlot: typeof IMPLEMENTATION_SLOT } {
  const account = poststate.get(vault.toLowerCase());
  if (!account) fail('missing poststate vault');
  const expectedRuntime = `0x${SMA_B_RUNTIME}${owner.slice(2).toLowerCase()}` as Hex;
  if (hex(account.code, 'poststate vault code') !== expectedRuntime) fail('poststate vault runtime mismatch');
  const storage = storageMap(account, 'poststate vault');
  const expectedImplementation = BigInt(IMPLEMENTATION);
  if (storage.size !== 1 || storage.get(IMPLEMENTATION_SLOT as Hex) !== expectedImplementation) fail('poststate vault implementation/storage mismatch');
  return { runtimeHash: keccak256(expectedRuntime), implementationSlot: IMPLEMENTATION_SLOT };
}

function parseCallTree(raw: unknown, transaction: PlainRecord): ParsedCallTree {
  let frames = 0;
  const nodes: TraceFrame[] = [];
  const entryPoint = address(transaction.to, 'transaction entry point');
  const from = address(transaction.from, 'transaction sender');
  const rootInput = hex(transaction.input, 'transaction input');
  if ((rootInput.length - 2) / 2 > MAX_CALLDATA_BYTES) fail('transaction input exceeds bound');
  function walk(frame: unknown, depth: number, parent: number | null): number {
    if (++frames > MAX_TRACE_FRAMES || depth > MAX_DEPTH) fail('call trace frame/depth bound exceeded');
    const object = plain(frame, 'call trace frame');
    if (typeof object.type !== 'string' || object.type.length === 0 || object.type.length > 32) fail('invalid call trace type');
    const current: TraceFrame = {
      index: nodes.length,
      parent,
      type: object.type,
      from: address(object.from, 'call trace from'),
      to: address(object.to, 'call trace to'),
      input: hex(object.input, 'call trace input'),
      output: hex(object.output ?? '0x', 'call trace output'),
      error: object.error === undefined || object.error === null ? null : String(object.error),
      gas: object.gas === undefined ? null : uint(object.gas, 'call trace gas').toString(),
      gasUsed: object.gasUsed === undefined ? null : uint(object.gasUsed, 'call trace gas used').toString(),
      value: object.value === undefined ? null : uint(object.value, 'call trace value').toString(),
      children: [],
    };
    if ((current.input.length - 2) / 2 > MAX_CALLDATA_BYTES || (current.output.length - 2) / 2 > MAX_CALLDATA_BYTES) fail('call trace payload exceeds bound');
    if (parent === null && (!isAddressEqual(current.to, entryPoint) || !isAddressEqual(current.from, from)
      || current.input !== rootInput || current.error !== null)) fail('call trace root mismatch');
    if (object.calls !== undefined && !Array.isArray(object.calls)) fail('invalid call trace children');
    nodes.push(current);
    for (const child of object.calls ?? []) current.children.push(walk(child, depth + 1, current.index));
    return current.index;
  }
  const rootIndex = walk(raw, 0, null);
  function redact(currentIndex: number): RedactedCall {
    const frame = nodes[currentIndex];
    if (!frame) return fail('missing call trace frame');
    return {
      type: frame.type,
      from: frame.from,
      to: frame.to,
      gas: frame.gas,
      gasUsed: frame.gasUsed,
      inputSelector: frame.input.length >= 10 ? frame.input.slice(0, 10) as Hex : null,
      inputHash: keccak256(frame.input),
      outputHash: keccak256(frame.output),
      reverted: frame.error !== null,
      calls: frame.children.map(redact),
    };
  }
  return { nodes, rootIndex, frames, redacted: redact(rootIndex) };
}

function sourceIdentity(rawTransaction: unknown, rawReceipt: unknown, rawBlock: unknown, transactionHash: Hex): SourceIdentity {
  const transaction = plain(rawTransaction, 'transaction');
  const receipt = plain(rawReceipt, 'receipt');
  const block = plain(rawBlock, 'block');
  const txHash = hash(transaction.hash, 'transaction hash');
  const receiptHash = hash(receipt.transactionHash, 'receipt transaction hash');
  if (txHash !== transactionHash || receiptHash !== transactionHash || transaction.blockHash === null || transaction.blockNumber === null
    || transaction.transactionIndex === null || receipt.blockHash === null || receipt.blockNumber === null || receipt.transactionIndex === null) fail('incomplete transaction/receipt identity');
  const blockHash = hash(transaction.blockHash, 'transaction block hash');
  const blockNumber = uint(transaction.blockNumber, 'transaction block number');
  const transactionIndex = index(transaction.transactionIndex, 'transaction index');
  const transactions = Array.isArray(block.transactions) ? block.transactions : fail('transaction/receipt/block identity mismatch');
  if (hash(receipt.blockHash, 'receipt block hash') !== blockHash || uint(receipt.blockNumber, 'receipt block number') !== blockNumber
    || index(receipt.transactionIndex, 'receipt transaction index') !== transactionIndex || uint(block.number, 'block number') !== blockNumber
    || hash(block.hash, 'block hash') !== blockHash || hash(transactions[transactionIndex], 'block transaction hash') !== transactionHash) fail('transaction/receipt/block identity mismatch');
  const entryPoint = address(transaction.to, 'transaction entry point');
  if (entryPoint.toLowerCase() !== ENTRY_POINT || receipt.to === null || !isAddressEqual(address(receipt.to, 'receipt entry point'), entryPoint)
    || transaction.value !== undefined && uint(transaction.value, 'transaction value') !== 0n) fail('direct pinned EntryPoint transaction required');
  return { transactionHash, blockHash, blockNumber: blockNumber.toString(), transactionIndex, entryPoint,
    inputHash: keccak256(hex(transaction.input, 'transaction input')) };
}

function normalizedPacked(value: unknown, label: string): CanonicalPacked {
  const record = plain(value, label);
  return {
    sender: address(record.sender, `${label} sender`),
    nonce: uint(record.nonce, `${label} nonce`),
    initCode: hex(record.initCode, `${label} initCode`),
    callData: hex(record.callData, `${label} callData`),
    accountGasLimits: hex(record.accountGasLimits, `${label} accountGasLimits`),
    preVerificationGas: uint(record.preVerificationGas, `${label} preVerificationGas`),
    gasFees: hex(record.gasFees, `${label} gasFees`),
    paymasterAndData: hex(record.paymasterAndData, `${label} paymasterAndData`),
    signature: hex(record.signature, `${label} signature`),
  };
}

function packedUint128Pair(value: Hex, label: string): readonly [bigint, bigint] {
  if (value.length !== 66) fail(`invalid ${label} length`);
  return [BigInt(`0x${value.slice(2, 34)}`), BigInt(`0x${value.slice(34)}`)];
}

function unpackUserOperation(packed: CanonicalPacked): UserOperation<'0.7'> {
  const [verificationGasLimit, callGasLimit] = packedUint128Pair(packed.accountGasLimits, 'operation accountGasLimits');
  const [maxPriorityFeePerGas, maxFeePerGas] = packedUint128Pair(packed.gasFees, 'operation gasFees');
  const factory = packed.initCode === '0x' ? undefined : address(packed.initCode.slice(0, 42), 'operation factory');
  const factoryData = packed.initCode.length === 42 ? undefined : hex(`0x${packed.initCode.slice(42)}`, 'operation factory data');
  return { sender: packed.sender, nonce: packed.nonce, callData: packed.callData, callGasLimit, verificationGasLimit,
    preVerificationGas: packed.preVerificationGas, maxFeePerGas, maxPriorityFeePerGas, factory, factoryData, signature: packed.signature };
}

function packedEqual(a: CanonicalPacked, b: CanonicalPacked): boolean {
  return isAddressEqual(a.sender, b.sender) && a.nonce === b.nonce && a.initCode === b.initCode && a.callData === b.callData
    && a.accountGasLimits === b.accountGasLimits && a.preVerificationGas === b.preVerificationGas && a.gasFees === b.gasFees
    && a.paymasterAndData === b.paymasterAndData && a.signature === b.signature;
}

function decodedArgs(value: unknown, label: string): readonly unknown[] {
  if (!Array.isArray(value)) fail(`invalid ${label}`);
  return value;
}

function terminalOperation(rawReceipt: unknown, rawTransaction: unknown, expected: SmaBFirstUserOpValidationTraceExpected,
  profile: 'counterfactual' | 'deployed' = 'counterfactual', allowFailure = false,
  expectedChainId: 4663 | 46630 = CHAIN_ID): ObservedOperation {
  const receipt = plain(rawReceipt, 'receipt');
  const transaction = plain(rawTransaction, 'transaction');
  const decoded = parsed('EntryPoint handleOps calldata', () => decodeFunctionData({ abi: entryPoint07Abi, data: hex(transaction.input, 'transaction input') }));
  const handleOpsArgs = decodedArgs(decoded.args, 'EntryPoint handleOps args');
  if (decoded.functionName !== 'handleOps' || !Array.isArray(handleOpsArgs[0]) || handleOpsArgs[0].length !== 1) fail('exactly one direct handleOps operation required');
  const packed = normalizedPacked(handleOpsArgs[0][0], 'packed operation');
  const unpacked = unpackUserOperation(packed);
  if (!isAddressEqual(unpacked.sender, expected.vault)) fail('operation vault mismatch');
  if (packed.paymasterAndData !== '0x') fail('paymaster operation unsupported');
  const { factory, factoryData } = unpacked;
  if (profile === 'counterfactual' && (!factory || !factoryData || !isAddressEqual(factory, expected.factory))) fail('counterfactual factory mismatch');
  if (profile === 'deployed' && packed.initCode !== '0x') fail('deployed operation requires empty initCode');
  if (keccak256(packed.callData) !== expected.callDataHash) fail('reviewed calldata mismatch');
  const expectedNonceKey = (expected.nonceLane << 40n) | 1n;
  if (packed.nonce >> 64n !== expectedNonceKey) fail('operation is not fallback-owner nonce lane');
  const signature = packed.signature;
  if (!/^0xff00[0-9a-f]{130}$/.test(signature)) fail('operation is not ff00 fallback EOA signature');
  if (profile === 'counterfactual') {
    const factoryCall = parsed('factory calldata', () => decodeFunctionData({ abi: factoryAbi, data: factoryData! }));
    const factoryArgs = decodedArgs(factoryCall.args, 'factory calldata args');
    if (factoryCall.functionName !== 'createSemiModularAccount' || !isAddressEqual(address(factoryArgs[0], 'factory owner'), expected.owner)
      || uint(factoryArgs[1], 'factory salt') !== expected.salt
      || factoryData !== encodeFunctionData({ abi: factoryAbi, functionName: 'createSemiModularAccount', args: [expected.owner, expected.salt] })) fail('factory creation binding mismatch');
  }
  const userOperationHash = parsed('user operation hash input', () => getUserOperationHash({ userOperation: unpacked, chainId: expectedChainId,
    entryPointAddress: ENTRY_POINT, entryPointVersion: '0.7' }));
  if (userOperationHash.toLowerCase() !== expected.userOperationHash) fail('user operation hash mismatch');
  const logs = Array.isArray(receipt.logs) ? receipt.logs : fail('invalid receipt logs');
  const events: PlainRecord[] = [];
  for (const rawLog of logs) {
    const log = plain(rawLog, 'receipt log');
    if (!isAddressEqual(address(log.address, 'receipt log address'), ENTRY_POINT)) continue;
    try {
      const topics = Array.isArray(log.topics) ? log.topics.map((topic, entry) => hex(topic, `receipt log topic ${entry}`)) : fail('invalid receipt log topics');
      if (topics.length === 0) fail('invalid receipt log topics');
      const event = decodeEventLog({ abi: entryPoint07Abi, eventName: 'UserOperationEvent', topics: topics as [Hex, ...Hex[]], data: hex(log.data, 'receipt log data'), strict: true });
      if (event.eventName === 'UserOperationEvent') events.push(plain(event.args, 'UserOperationEvent args'));
    } catch { /* Other EntryPoint accounting/marker events are not operation terminals. */ }
  }
  const event = events[0];
  if (receipt.status !== 'success' || events.length !== 1 || !event || typeof event.success !== 'boolean' || !allowFailure && !event.success
    || !isAddressEqual(address(event.sender, 'terminal sender'), expected.vault)
    || uint(event.nonce, 'terminal nonce') !== packed.nonce
    || hash(event.userOpHash, 'terminal hash') !== userOperationHash.toLowerCase()) fail('single successful UserOperationEvent required');
  return { packed, factory, factoryData, userOperationHash, signatureCommitment: keccak256(signature), success: event.success };
}

async function snapshot(client: SmaBFirstUserOpValidationTraceClient, transactionHash: Hex, concurrentReads = false): Promise<{ transaction: unknown; receipt: unknown; block: unknown }> {
  const [transaction, receipt] = concurrentReads
    ? await Promise.all([client.getTransaction({ hash: transactionHash }), client.getTransactionReceipt({ hash: transactionHash })])
    : [await client.getTransaction({ hash: transactionHash }), await client.getTransactionReceipt({ hash: transactionHash })];
  const transactionRecord = plain(transaction, 'transaction');
  if (!transaction || !receipt || transactionRecord.blockNumber === null || transactionRecord.blockNumber === undefined) fail('transaction or receipt unavailable');
  const block = await client.getBlock({ blockNumber: uint(transactionRecord.blockNumber, 'transaction block number') });
  if (!block || plain(block, 'block').hash === null) fail('source block unavailable');
  return { transaction: structuredClone(transaction), receipt: structuredClone(receipt), block: structuredClone(block) };
}

async function localTrace(local: SmaBFirstUserOpValidationTraceLocal, options: PlainRecord, transactionHash: Hex): Promise<unknown> {
  try {
    const trace = await local('debug_traceTransaction', [transactionHash, options], 20_000);
    if (trace === null || trace === undefined) fail('debug_traceTransaction returned no trace');
    return trace;
  } catch (error) {
    const detail = error instanceof Error ? error.message.replace(/0x[0-9a-fA-F]{130,}/g, '[hex payload omitted]') : 'unknown error';
    return fail(`debug_traceTransaction unavailable: ${detail.slice(0, 300)}`);
  }
}

function expectedInput(raw: SmaBFirstUserOpValidationTraceExpected): SmaBFirstUserOpValidationTraceExpected {
  const value = plain(raw, 'expected');
  const result: SmaBFirstUserOpValidationTraceExpected = {
    vault: address(value.vault, 'expected vault'),
    owner: address(value.owner, 'expected owner'),
    userOperationHash: hash(value.userOperationHash, 'expected user operation hash'),
    nonceLane: typeof value.nonceLane === 'bigint' ? value.nonceLane : fail('expected nonceLane must be bigint'),
    factory: address(value.factory, 'expected factory'),
    implementation: address(value.implementation, 'expected implementation'),
    salt: typeof value.salt === 'bigint' ? value.salt : fail('expected salt must be bigint'),
    callDataHash: hash(value.callDataHash, 'expected calldata hash'),
  };
  if (result.vault.toLowerCase() === result.owner.toLowerCase() || result.owner === getAddress('0x0000000000000000000000000000000000000000')
    || result.nonceLane <= 0n || result.nonceLane >= 1n << 152n || result.salt < 0n || result.salt >= 1n << 256n) fail('invalid expected identity');
  if (result.factory.toLowerCase() !== FACTORY || result.implementation.toLowerCase() !== IMPLEMENTATION) fail('unexpected SMA-B pins');
  return result;
}

function expectedInitCode(expected: SmaBFirstUserOpValidationTraceExpected): Hex {
  return `0x${SMA_B_INIT_PREFIX}${expected.implementation.slice(2).toLowerCase()}${SMA_B_INIT_SUFFIX}${expected.owner.slice(2).toLowerCase()}` as Hex;
}

function expectedVaultAddress(expected: SmaBFirstUserOpValidationTraceExpected, initCode: Hex): Address {
  const salt = keccak256(encodePacked(['address', 'uint256', 'uint32'], [expected.owner, expected.salt, 0xffffffff]));
  return getContractAddress({ from: expected.factory, opcode: 'CREATE2', salt, bytecode: initCode });
}

function traceSemantics(tree: ParsedCallTree, operation: ObservedOperation, expected: SmaBFirstUserOpValidationTraceExpected): TraceSemantics {
  const { nodes, rootIndex } = tree;
  const factoryFrames = nodes.filter(frame => frame.type === 'CALL' && isAddressEqual(frame.to, expected.factory) && frame.input === operation.factoryData);
  if (factoryFrames.length !== 1) fail('exactly one factory creation frame required');
  const factory = factoryFrames[0];
  if (!factory) fail('missing factory creation frame');
  const senderCreator = factory.parent === null ? undefined : nodes[factory.parent];
  if (!senderCreator || senderCreator.parent !== rootIndex || senderCreator.type !== 'CALL' || !isAddressEqual(senderCreator.from, ENTRY_POINT)
    || !isAddressEqual(factory.from, senderCreator.to)) fail('unexpected SenderCreator/factory trace path');
  const initCode = expectedInitCode(expected);
  if (!isAddressEqual(expectedVaultAddress(expected, initCode), expected.vault)) fail('factory CREATE2 address prediction mismatch');
  const creationFrames = nodes.filter(frame => frame.type === 'CREATE2' && isAddressEqual(frame.from, expected.factory)
    && isAddressEqual(frame.to, expected.vault) && frame.input === initCode);
  if (creationFrames.length !== 1 || creationFrames[0]?.parent !== factory.index || creationFrames[0]?.error !== null) fail('exactly one bound factory CREATE2 required');
  const creation = creationFrames[0];
  if (!creation) fail('missing factory CREATE2 frame');
  const validationFrames = nodes.filter(frame => frame.type === 'CALL' && frame.parent === rootIndex && isAddressEqual(frame.from, ENTRY_POINT)
    && isAddressEqual(frame.to, expected.vault) && frame.input.slice(0, 10) === '0x19822f7c');
  if (validationFrames.length !== 1) fail('exactly one direct validateUserOp frame required');
  const validation = validationFrames[0];
  if (!validation) fail('missing validation frame');
  if (creation.index >= validation.index || validation.error !== null) fail('factory creation must precede successful validation');
  for (const frame of nodes.slice(creation.index + 1, validation.index)) {
    if (isAddressEqual(frame.to, expected.vault) || isAddressEqual(frame.from, expected.vault)) fail('vault touched between creation and validation');
  }
  const decodedValidation = parsed('validateUserOp calldata', () => decodeFunctionData({ abi: validateAbi, data: validation.input }));
  const validationArgs = decodedArgs(decodedValidation.args, 'validateUserOp calldata args');
  if (decodedValidation.functionName !== 'validateUserOp' || !packedEqual(operation.packed, normalizedPacked(validationArgs[0], 'validation operation'))
    || hash(validationArgs[1], 'validation user operation hash') !== operation.userOperationHash.toLowerCase()) fail('validateUserOp input does not bind selected operation');
  if (validation.output.length !== 66 || parsed('validateUserOp result', () => decodeFunctionResult({ abi: validateAbi, functionName: 'validateUserOp', data: validation.output })) !== 0n) fail('validateUserOp did not return passed validationData');
  const validationDelegate = validation.children.map(child => nodes[child]).filter((frame): frame is TraceFrame => frame !== undefined)
    .filter(frame => frame.type === 'DELEGATECALL' && isAddressEqual(frame.from, expected.vault) && isAddressEqual(frame.to, expected.implementation)
      && frame.input === validation.input && frame.error === null);
  if (validationDelegate.length !== 1 || validationDelegate[0]?.output !== validation.output) fail('pinned implementation validation delegatecall mismatch');
  const executionWrappers = nodes.filter(frame => frame.type === 'CALL' && frame.parent === rootIndex && isAddressEqual(frame.from, ENTRY_POINT)
    && isAddressEqual(frame.to, ENTRY_POINT) && frame.input.slice(0, 10) === '0x0042dc53' && frame.error === null);
  if (executionWrappers.length !== 1 || !executionWrappers[0] || executionWrappers[0].index <= validation.index) fail('exactly one ordered EntryPoint execution wrapper required');
  const wrapper = executionWrappers[0];
  const executionFrames = nodes.filter(frame => frame.type === 'CALL' && frame.parent === wrapper.index && isAddressEqual(frame.from, ENTRY_POINT)
    && isAddressEqual(frame.to, expected.vault) && frame.input === operation.packed.callData);
  if (executionFrames.length !== 1 || !executionFrames[0] || executionFrames[0].index <= validation.index || executionFrames[0].error !== null) fail('exactly one ordered execution frame required');
  const execution = executionFrames[0];
  const executionDelegate = execution.children.map(child => nodes[child]).filter((frame): frame is TraceFrame => frame !== undefined)
    .filter(frame => frame.type === 'DELEGATECALL' && isAddressEqual(frame.from, expected.vault) && isAddressEqual(frame.to, expected.implementation)
      && frame.input === execution.input && frame.error === null);
  if (executionDelegate.length !== 1) fail('pinned implementation execution delegatecall mismatch');
  const inboundVault = nodes.filter(frame => isAddressEqual(frame.to, expected.vault));
  if (inboundVault.length !== 3 || !inboundVault.every(frame => frame === creation || frame === validation || frame === execution)) fail('vault management/reentry trace is unsupported');
  const root = nodes[rootIndex];
  if (!root || root.error !== null) fail('handleOps trace reverted');
  return { factoryInputHash: keccak256(factory.input), create2InitCodeHash: keccak256(initCode),
    validationInputHash: keccak256(validation.input), validationOutputHash: keccak256(validation.output), executionInputHash: keccak256(execution.input) };
}

function chainId(value: unknown, label: string): bigint {
  if (typeof value === 'number') {
    if (!Number.isSafeInteger(value) || value < 0) fail(`invalid ${label}`);
    return BigInt(value);
  }
  return uint(value, label);
}

/**
 * Collects bounded local facts for exactly one counterfactual SMA-B v2.0.0
 * fallback-EOA operation. It never signs, sends, stores, settles, or authorizes.
 */
export function collectSmaBFirstUserOpValidationTrace(input: SmaBFirstUserOpValidationTraceInput): Promise<SmaBFirstUserOpValidationTraceEvidence> {
  return collectFirstTraceForChain(input, CHAIN_ID);
}

/** Active terminal observer only; the build, never the caller, selects its domain. */
export function collectSelectedSmaBFirstUserOpValidationTrace(input: SmaBFirstUserOpValidationTraceInput) {
  return collectFirstTraceForChain(input, SELECTED_ACCOUNT_CHAIN_ID);
}

async function collectFirstTraceForChain<ChainId extends 4663 | 46630>(
  { local, client, transactionHash, expected }: SmaBFirstUserOpValidationTraceInput,
  expectedChainId: ChainId,
): Promise<SmaBFirstUserOpValidationTraceEvidence<ChainId>> {
  if (typeof local !== 'function') fail('local RPC function required');
  if (!client || typeof client !== 'object') fail('public client required');
  const sourceHash = hash(transactionHash, 'transaction hash');
  const input = expectedInput(expected);
  const before = await snapshot(client, sourceHash);
  if (chainId(await client.getChainId(), 'client chainId') !== BigInt(expectedChainId) || chainId(await local('eth_chainId', [], 20_000), 'local chainId') !== BigInt(expectedChainId)) fail('local/client chain mismatch');
  const identity = sourceIdentity(before.transaction, before.receipt, before.block, sourceHash);
  const operation = terminalOperation(before.receipt, before.transaction, input, 'counterfactual', false, expectedChainId);
  if (!await parsedAsync('ff00 signature', () => verifyMessage({ address: input.owner, message: { raw: operation.userOperationHash }, signature: `0x${operation.packed.signature.slice(6)}` as Hex }))) fail('ff00 signature does not recover expected owner');

  const prestateRaw = await localTrace(local, { tracer: 'prestateTracer', timeout: '5s' }, sourceHash);
  const diffRaw = await localTrace(local, { tracer: 'prestateTracer', tracerConfig: { diffMode: true }, timeout: '5s' }, sourceHash);
  const callRaw = await localTrace(local, { tracer: 'callTracer', tracerConfig: { withLog: true }, timeout: '5s' }, sourceHash);
  const traceCommitments = { fullPrestate: commitment(prestateRaw, 'full prestate trace'), diff: commitment(diffRaw, 'diff trace'), call: commitment(callRaw, 'call trace') };
  const prestate = addressMap(prestateRaw, 'full prestate');
  prestateCode(prestate, ENTRY_POINT, ENTRY_POINT_CODE_HASH, 'EntryPoint');
  prestateCode(prestate, input.factory, FACTORY_CODE_HASH, 'factory');
  prestateCode(prestate, input.implementation, IMPLEMENTATION_CODE_HASH, 'SMA-B implementation');
  emptyCounterfactual(prestate.get(input.vault.toLowerCase()), 'full prestate vault');
  const diffObject = plain(diffRaw, 'diff trace');
  if (!Object.hasOwn(diffObject, 'pre') || !Object.hasOwn(diffObject, 'post')) fail('diff trace pre/post required');
  const diffPre = addressMap(diffObject.pre, 'diff prestate');
  const diffPost = addressMap(diffObject.post, 'diff poststate');
  emptyCounterfactual(diffPre.get(input.vault.toLowerCase()), 'diff prestate vault');
  const deployment = postDeployment(diffPost, input.vault, input.owner);
  const tree = parseCallTree(callRaw, plain(before.transaction, 'transaction'));
  const trace = traceSemantics(tree, operation, input);

  const after = await snapshot(client, sourceHash);
  const rechecked = sourceIdentity(after.transaction, after.receipt, after.block, sourceHash);
  if (JSON.stringify(identity) !== JSON.stringify(rechecked) || terminalOperation(after.receipt, after.transaction, input, 'counterfactual', false, expectedChainId).userOperationHash !== operation.userOperationHash
    || chainId(await client.getChainId(), 'client chainId') !== BigInt(expectedChainId) || chainId(await local('eth_chainId', [], 20_000), 'local chainId') !== BigInt(expectedChainId)) fail('source changed during trace collection');
  const evidence: SmaBFirstUserOpValidationTraceEvidence<ChainId> = { schemaVersion: 1, scope: 'local-sma-b-v2.0.0-first-deployment-fallback-eoa-validation-trace', chainId: expectedChainId,
    source: { ...identity, userOperationHash: operation.userOperationHash, nonce: operation.packed.nonce.toString(), traceCommitments,
      traceProfile: { prestate: 'prestateTracer' as const, diff: 'prestateTracer.diffMode' as const, call: 'callTracer.withLog' as const }, callTrace: tree.redacted,
      traceNotCryptographicProof: true as const },
    validation: { kind: 'sma-b-v2.0.0-counterfactual-fallback-eoa-observed', vault: input.vault, owner: input.owner,
      nonceLane: input.nonceLane.toString(), lookupKey: '0', globalValidation: true, deferredAction: false, directCallValidation: false,
      signatureFormat: 'ff00-eoa-65-byte-redacted', signatureCommitment: operation.signatureCommitment,
      factory: { address: input.factory, inputHash: trace.factoryInputHash, create2InitCodeHash: trace.create2InitCodeHash },
      implementation: { address: input.implementation, runtimeHash: deployment.runtimeHash, implementationSlot: deployment.implementationSlot },
      validateUserOp: { inputHash: trace.validationInputHash, outputHash: trace.validationOutputHash, validationData: '0' },
      executionInputHash: trace.executionInputHash },
    traceBounds: { maxBytesPerTrace: MAX_TRACE_BYTES, maxAccounts: MAX_ACCOUNTS, maxStorageSlotsPerAccount: MAX_STORAGE_SLOTS,
      maxFrames: MAX_TRACE_FRAMES, maxDepth: MAX_DEPTH, observedFrames: tree.frames },
    limitations: ['Only one direct EntryPoint v0.7 handleOps counterfactual SMA-B v2.0.0 fallback EOA operation is observed.',
      'Trusted local debug traces are not cryptographic proofs and do not establish remote-provider finality.',
      'Existing/rotated vaults, nonzero validation entities or hooks, deferred/direct validation, ERC-1271 owners, paymasters, aggregation, upgrades, and multi-operation bundles are unsupported.',
      'No persistence, settlement, movements, fee, P&L, referral, points, signing, submission, or publication authority.'],
    ...falseAuthorities };
  return Object.freeze(evidence);
}

export interface SmaBDeployedUserOpValidationTraceExpected extends SmaBFirstUserOpValidationTraceExpected {
  /** Immutable creation provenance; owner is the signer expected at execution. */
  originalOwner: Address;
}

export interface SmaBDeployedUserOpValidationTraceInput extends Omit<SmaBFirstUserOpValidationTraceInput, 'expected'> {
  expected: SmaBDeployedUserOpValidationTraceExpected;
}

export interface SmaBDeployedUserOpValidationTraceEvidence<ChainId extends 4663 | 46630 = 4663> extends Omit<SmaBFirstUserOpValidationTraceEvidence<ChainId>, 'scope' | 'validation'> {
  readonly scope: 'local-sma-b-v2.0.0-deployed-fallback-eoa-validation-trace';
  readonly validation: Omit<SmaBFirstUserOpValidationTraceEvidence['validation'], 'kind' | 'factory'> & Readonly<{
    kind: 'sma-b-v2.0.0-deployed-fallback-eoa-observed';
    factory: Readonly<{ address: Address; create2InitCodeHash: Hex; provenanceOnly: true }>;
    ownerResolution: Readonly<{
      originalOwner: Address;
      storedFallbackSigner: Address;
      fallbackSignerDisabled: false;
      source: 'appended-original-owner' | 'stored-fallback-signer';
      packedStorageSlot: typeof FALLBACK_SIGNER_SLOT;
      packedStorageValue: Hex;
    }>;
    ecrecover: Readonly<{ inputHash: Hex; outputHash: Hex; recoveredOwner: Address }>;
  }>;
}

function deployedExpectedInput(raw: SmaBDeployedUserOpValidationTraceExpected): SmaBDeployedUserOpValidationTraceExpected {
  const originalOwner = address(plain(raw, 'expected').originalOwner, 'expected original owner');
  const input = { ...expectedInput(raw), originalOwner };
  if (BigInt(originalOwner) === 0n || isAddressEqual(input.vault, originalOwner)) fail('invalid expected original owner');
  const provenance = { ...input, owner: originalOwner };
  if (!isAddressEqual(expectedVaultAddress(provenance, expectedInitCode(provenance)), input.vault)) fail('factory CREATE2 address prediction mismatch');
  return input;
}

function deployedPrestate(prestate: Map<string, PlainRecord>, input: SmaBDeployedUserOpValidationTraceExpected): {
  runtimeHash: Hex;
  ownerResolution: SmaBDeployedUserOpValidationTraceEvidence['validation']['ownerResolution'];
} {
  prestateCode(prestate, ENTRY_POINT, ENTRY_POINT_CODE_HASH, 'EntryPoint');
  prestateCode(prestate, input.implementation, IMPLEMENTATION_CODE_HASH, 'SMA-B implementation');
  const account = prestate.get(input.vault.toLowerCase());
  if (!account) fail('missing prestate deployed vault');
  const runtime = `0x${SMA_B_RUNTIME}${input.originalOwner.slice(2).toLowerCase()}` as Hex;
  if (hex(account.code, 'prestate deployed vault code') !== runtime) fail('prestate deployed vault runtime mismatch');
  const storage = storageMap(account, 'prestate deployed vault');
  if (storage.get(IMPLEMENTATION_SLOT) !== BigInt(input.implementation)) fail('prestate deployed implementation slot mismatch');
  // prestateTracer includes accessed slots, not all installed state. Never infer
  // an absent slot is zero or claim that untouched modules do not exist.
  const packed = storage.get(FALLBACK_SIGNER_SLOT);
  if (packed === undefined) fail('missing accessed fallback signer slot');
  if ((packed >> 160n & 0xffn) !== 0n) fail('prestate fallback signer disabled');
  const storedFallbackSigner = address(`0x${(packed & ((1n << 160n) - 1n)).toString(16).padStart(40, '0')}`, 'stored fallback signer');
  const source = BigInt(storedFallbackSigner) === 0n ? 'appended-original-owner' : 'stored-fallback-signer';
  const owner = source === 'appended-original-owner' ? input.originalOwner : storedFallbackSigner;
  if (!isAddressEqual(owner, input.owner)) fail('execution-time fallback owner mismatch');
  return { runtimeHash: keccak256(runtime), ownerResolution: { originalOwner: input.originalOwner, storedFallbackSigner,
    fallbackSignerDisabled: false, source, packedStorageSlot: FALLBACK_SIGNER_SLOT,
    packedStorageValue: `0x${packed.toString(16).padStart(64, '0')}` as Hex } };
}

function unchangedDeployedState(raw: unknown, prestate: Map<string, PlainRecord>, input: SmaBDeployedUserOpValidationTraceExpected, first = false): void {
  const diff = plain(raw, 'diff trace');
  if (!Object.hasOwn(diff, 'pre') || !Object.hasOwn(diff, 'post')) fail('diff trace pre/post required');
  const pre = addressMap(diff.pre, 'diff prestate');
  const post = addressMap(diff.post, 'diff poststate');
  for (const map of [prestate, pre, post]) {
    for (const account of map.values()) storageMap(account, 'deployed trace account', true);
  }
  // A native execute/executeBatch cannot legitimately change vault storage.
  // Net-state checks alone cannot exclude temporary changes: the route checks
  // below also exclude native management entry, self calls and foreign delegates.
  const protectedTargets: readonly Address[] = first ? [ENTRY_POINT, input.implementation, input.factory] : [input.vault, ENTRY_POINT, input.implementation];
  for (const target of protectedTargets) {
    const before = pre.get(target.toLowerCase());
    const after = post.get(target.toLowerCase());
    const original = prestate.get(target.toLowerCase());
    const label = isAddressEqual(target, input.vault) ? 'vault authority/storage' : 'pinned code';
    const creationFactory = first && isAddressEqual(target, input.factory);
    if (!original || Boolean(before) !== Boolean(after)) fail(`in-transaction ${label} change unsupported`);
    // Geth diff.pre retains unchanged basic fields. diff.post contains changed
    // fields only; a pre-only account means deletion, a post-only one insertion.
    // Compare explicit pre fields without inventing an omitted nonce value.
    if (before && (Object.hasOwn(before, 'code') && hex(before.code, 'diff pre code') !== hex(original.code, 'full pre code')
      || Object.hasOwn(before, 'nonce') && (!Object.hasOwn(original, 'nonce')
        || traceNonce(before.nonce, 'diff pre nonce') !== traceNonce(original.nonce, 'full pre nonce')))
      || after && (Object.hasOwn(after, 'code') || !creationFactory && Object.hasOwn(after, 'nonce'))) fail(`in-transaction ${label} change unsupported`);
    // CREATE2 increments the creator's nonce even though its address formula
    // uses salt. Preserve exactly that one observed change for first operations.
    if (creationFactory && (!after || !Object.hasOwn(original, 'nonce') || !Object.hasOwn(after, 'nonce')
      || traceNonce(after.nonce, 'factory post nonce') !== traceNonce(original.nonce, 'factory pre nonce') + 1n)) fail('first-operation factory nonce increment mismatch');
    if ((isAddressEqual(target, input.vault) || creationFactory)
      && (before && storageMap(before, 'diff deployed vault', true).size !== 0
        || after && storageMap(after, 'diff deployed vault', true).size !== 0)) fail('in-transaction vault authority/storage change unsupported');
  }
}

interface DeployedTraceSemantics {
  validationInputHash: Hex;
  validationOutputHash: Hex;
  executionInputHash: Hex;
  ecrecover: SmaBDeployedUserOpValidationTraceEvidence['validation']['ecrecover'];
  prefundPaid: bigint;
  nativeReceipts: readonly bigint[];
}

function deployedTraceSemantics(tree: ParsedCallTree, operation: ObservedOperation,
  input: SmaBDeployedUserOpValidationTraceExpected,
  native?: { trade: Parameters<typeof decodeAndValidateNativeV2Trade>[0]; decoded: ReturnType<typeof decodeAndValidateNativeV2Trade> | undefined },
  creation?: TraceFrame): DeployedTraceSemantics {
  const { nodes, rootIndex } = tree;
  const children = (frame: TraceFrame): TraceFrame[] => frame.children.map(child => nodes[child] ?? fail('missing trace child'));
  const validationFrames = nodes.filter(frame => frame.type === 'CALL' && frame.parent === rootIndex && isAddressEqual(frame.from, ENTRY_POINT)
    && isAddressEqual(frame.to, input.vault) && frame.input.slice(0, 10) === '0x19822f7c');
  const validation = validationFrames[0];
  if (validationFrames.length !== 1 || !validation || validation.error !== null) fail('exactly one successful direct validateUserOp frame required');
  if (creation && (creation.index >= validation.index || nodes.slice(creation.index + 1, validation.index)
    .some(frame => isAddressEqual(frame.to, input.vault) || isAddressEqual(frame.from, input.vault)))) fail('creation must immediately precede vault validation');
  const decoded = parsed('validateUserOp calldata', () => decodeFunctionData({ abi: validateAbi, data: validation.input }));
  const args = decodedArgs(decoded.args, 'validateUserOp calldata args');
  if (decoded.functionName !== 'validateUserOp' || !packedEqual(operation.packed, normalizedPacked(args[0], 'validation operation'))
    || hash(args[1], 'validation user operation hash') !== operation.userOperationHash.toLowerCase()) fail('validateUserOp input does not bind selected operation');
  const missingAccountFunds = uint(args[2], 'missing account funds');
  if (validation.output.length !== 66 || parsed('validateUserOp result', () => decodeFunctionResult({ abi: validateAbi,
    functionName: 'validateUserOp', data: validation.output })) !== 0n) fail('validateUserOp did not return passed validationData');
  const validationDelegate = children(validation)[0];
  if (validation.children.length !== 1 || !validationDelegate || validationDelegate.type !== 'DELEGATECALL'
    || !isAddressEqual(validationDelegate.from, input.vault) || !isAddressEqual(validationDelegate.to, input.implementation)
    || validationDelegate.input !== validation.input || validationDelegate.output !== validation.output || validationDelegate.error !== null) fail('pinned implementation validation delegatecall mismatch');

  const sig = operation.packed.signature.slice(6);
  const recoveryId = BigInt(`0x${sig.slice(128)}`);
  // Match OpenZeppelin ECDSA's 65-byte, low-s, 27/28 route before accepting the
  // precompile transcript, even if a more permissive offchain recovery accepts it.
  if ((recoveryId !== 27n && recoveryId !== 28n) || BigInt(`0x${sig.slice(64, 128)}`)
    > 0x7fffffffffffffffffffffffffffffff5d576e7357a4501ddfe92f46681b20a0n) fail('unsupported fallback EOA signature encoding');
  const recoverInput = `${hashMessage({ raw: operation.userOperationHash })}${recoveryId.toString(16).padStart(64, '0')}${sig.slice(0, 128)}`;
  const recoverOutput = `0x${input.owner.slice(2).toLowerCase().padStart(64, '0')}`;
  const validationCalls = children(validationDelegate);
  const recover = validationCalls[0];
  if (!recover || recover.type !== 'STATICCALL' || !isAddressEqual(recover.from, input.vault)
    || BigInt(recover.to) !== 1n || recover.input !== recoverInput || recover.output !== recoverOutput
    || recover.error !== null || recover.children.length !== 0) fail('genuine bound fallback EOA ecrecover trace required');
  const prefund = validationCalls[1];
  if (validationCalls.length !== (missingAccountFunds === 0n ? 1 : 2)
    || missingAccountFunds !== 0n && (!prefund || prefund.type !== 'CALL' || !isAddressEqual(prefund.from, input.vault)
      || !isAddressEqual(prefund.to, ENTRY_POINT) || prefund.input !== '0x' || prefund.output !== '0x'
      || prefund.value !== missingAccountFunds.toString() || prefund.error !== null || prefund.children.length !== 0)) fail('unexpected validation module/hook/prefund route');

  const wrappers = nodes.filter(frame => frame.type === 'CALL' && frame.parent === rootIndex && isAddressEqual(frame.from, ENTRY_POINT)
    && isAddressEqual(frame.to, ENTRY_POINT) && frame.input.slice(0, 10) === '0x0042dc53' && frame.error === null);
  const wrapper = wrappers[0];
  if (wrappers.length !== 1 || !wrapper || wrapper.index <= validation.index) fail('exactly one ordered EntryPoint execution wrapper required');
  const execution = children(wrapper)[0];
  if (wrapper.children.length !== 1 || !execution || execution.type !== 'CALL' || !isAddressEqual(execution.from, ENTRY_POINT)
    || !isAddressEqual(execution.to, input.vault) || execution.input !== operation.packed.callData
    || (execution.error === null) !== operation.success) fail('exactly one bound execution frame required');
  const executionDelegate = children(execution)[0];
  if (execution.children.length !== 1 || !executionDelegate || executionDelegate.type !== 'DELEGATECALL'
    || !isAddressEqual(executionDelegate.from, input.vault) || !isAddressEqual(executionDelegate.to, input.implementation)
    || executionDelegate.input !== execution.input || executionDelegate.output !== execution.output
    || (executionDelegate.error === null) !== operation.success) fail('pinned implementation execution delegatecall mismatch');
  const executionCall = parsed('native execute/executeBatch calldata', () => decodeFunctionData({ abi: executionAbi, data: execution.input }));
  const executionArgs = decodedArgs(executionCall.args, 'native execution args');
  const calls = executionCall.functionName === 'execute'
    ? [{ target: executionArgs[0], value: executionArgs[1], data: executionArgs[2] }]
    : executionCall.functionName === 'executeBatch' && Array.isArray(executionArgs[0]) ? executionArgs[0] : fail('native execution route required');
  const reviewedNative = native?.decoded, nativeTrade = native?.trade;
  if (nativeTrade && (calls.length !== nativeTrade.calls.length || calls.some((value, index) => {
    const call = plain(value, 'native execution call'), reviewed = nativeTrade.calls[index]!;
    return !isAddressEqual(address(call.target, 'native execution target'), reviewed.to)
      || hex(call.data, 'native execution data') !== reviewed.data.toLowerCase()
      || uint(call.value, 'native execution value') !== reviewed.value;
  }))) fail('native trade does not bind signed execute calldata');
  if (!operation.success) {
    // The exact signed execution subtree reverted as a unit. Its attempted
    // transfers/logs are not committed effects; only validation and gas survive.
    // Do not accept an unrelated successful vault/implementation entry outside
    // that subtree. Net principal conservation is checked by the cashflow port.
    const underExecution = (frame: TraceFrame) => {
      let current: TraceFrame | undefined = frame;
      while (current) { if (current === execution) return true; current = current.parent === null ? undefined : nodes[current.parent]; }
      return false;
    };
    const allowed = new Set([validation, validationDelegate, recover, ...(prefund ? [prefund] : []), ...(creation ? [creation] : [])]);
    if (nodes.some(frame => !underExecution(frame) && !allowed.has(frame)
      && (isAddressEqual(frame.to, input.vault) || isAddressEqual(frame.from, input.vault)
        || isAddressEqual(frame.to, input.implementation)))) fail('reverted operation has unrelated vault entry');
    return { validationInputHash: keccak256(validation.input), validationOutputHash: keccak256(validation.output),
      executionInputHash: keccak256(execution.input), ecrecover: { inputHash: keccak256(recover.input),
        outputHash: keccak256(recover.output), recoveredOwner: input.owner }, prefundPaid: missingAccountFunds, nativeReceipts: [] };
  }
  const executionCalls = children(executionDelegate);
  if (calls.length === 0 || calls.length !== executionCalls.length) fail('unexpected execution module/hook route');
  for (let i = 0; i < calls.length; i++) {
    const call = plain(calls[i], 'native execution call');
    const frame = executionCalls[i];
    const target = address(call.target, 'native execution target');
    if (isAddressEqual(target, input.vault) || isAddressEqual(target, input.implementation) || isAddressEqual(target, ENTRY_POINT)) fail('vault management/reentry target unsupported');
    if (!frame || frame.type !== 'CALL' || !isAddressEqual(frame.from, input.vault) || !isAddressEqual(frame.to, target)
      || frame.input !== hex(call.data, 'native execution data') || frame.value !== uint(call.value, 'native execution value').toString()
      || frame.error !== null) fail('native execution child does not bind selected call');
  }
  const receives: TraceFrame[] = [], receiveDelegates: TraceFrame[] = [];
  if (reviewedNative?.nativeDirection === 'OUTPUT') {
    const router = address(nativeTrade!.policy.router, 'native router');
    const routerFrame = executionCalls.at(-1);
    if (!routerFrame || !isAddressEqual(routerFrame.to, router)) fail('native payout router execution missing');
    for (const frame of nodes.filter(frame => isAddressEqual(frame.to, input.vault) && frame !== validation && frame !== execution && frame !== creation)) {
      // Only this selected execution's router payout may enter receive(). Never
      // broaden owner/module/reentry permissions for arbitrary incoming ETH.
      const delegate = children(frame)[0];
      if (frame.type !== 'CALL' || frame.parent !== routerFrame.index || !isAddressEqual(frame.from, router)
        || frame.input !== '0x' || frame.output !== '0x' || frame.error !== null || frame.value === null
        || BigInt(frame.value) < 0n || frame.children.length !== 1 || !delegate
        || delegate.type !== 'DELEGATECALL' || !isAddressEqual(delegate.from, input.vault)
        || !isAddressEqual(delegate.to, input.implementation) || delegate.input !== '0x' || delegate.output !== '0x'
        || delegate.error !== null || delegate.children.length !== 0
        || delegate.value !== null && delegate.value !== frame.value) fail('native payout receive delegate mismatch');
      receives.push(frame); receiveDelegates.push(delegate);
    }
    const maximum = isAddressEqual(address(nativeTrade!.policy.feeRecipient, 'native fee recipient'), input.vault) ? 2 : 1;
    if (receives.length !== maximum) fail('native payout receive count mismatch');
  }
  const inboundVault = nodes.filter(frame => isAddressEqual(frame.to, input.vault));
  if (inboundVault.length !== 2 + receives.length + (creation ? 1 : 0)
    || !inboundVault.every(frame => frame === validation || frame === execution || frame === creation || receives.includes(frame))) fail('vault management/reentry trace is unsupported');
  const allowedOutbound = new Set([validationDelegate, executionDelegate, recover, ...executionCalls, ...receiveDelegates, ...(prefund ? [prefund] : [])]);
  if (nodes.some(frame => isAddressEqual(frame.from, input.vault) && !allowedOutbound.has(frame))) fail('unexpected vault module/delegate route');
  // Legacy permits only the two selected delegates. The explicit native branch
  // adds only the bound empty receive delegates, never another module route.
  if (nodes.some(frame => isAddressEqual(frame.to, input.implementation) && frame !== validationDelegate && frame !== executionDelegate && !receiveDelegates.includes(frame))) fail('unexpected implementation route');
  if (nodes[rootIndex]?.type !== 'CALL' || nodes[rootIndex]?.error !== null) fail('handleOps trace reverted');
  return { validationInputHash: keccak256(validation.input), validationOutputHash: keccak256(validation.output),
    executionInputHash: keccak256(execution.input), ecrecover: { inputHash: keccak256(recover.input),
      outputHash: keccak256(recover.output), recoveredOwner: input.owner }, prefundPaid: missingAccountFunds,
    nativeReceipts: receives.map(frame => BigInt(frame.value!)) };
}

/** Observes a deployed/rotated fallback EOA route; creates no execution authority. */
export async function collectSmaBDeployedUserOpValidationTrace({ local, client, transactionHash, expected }: SmaBDeployedUserOpValidationTraceInput): Promise<SmaBDeployedUserOpValidationTraceEvidence> {
  return (await collectDeployedTrace({ local, client, transactionHash, expected }, CHAIN_ID)).evidence;
}

/** Exact selected counterpart; legacy callers remain bound to4663. */
export async function collectSelectedSmaBDeployedUserOpValidationTrace(input: SmaBDeployedUserOpValidationTraceInput) {
  return (await collectDeployedTrace(input, SELECTED_ACCOUNT_CHAIN_ID)).evidence;
}

/** Deployed-vault native receipt prerequisite only; no first-operation support.
 * The payout exception is derived from actual signed calls and the native
 * decoder. A caller's direction/recipient/verified boolean is not authority. */
export async function collectNativeSmaBDeployedUserOpValidationTrace(input: SmaBDeployedUserOpValidationTraceInput & {
  nativeTrade: Parameters<typeof decodeAndValidateNativeV2Trade>[0];
}) {
  const nativeTrade = structuredClone(input.nativeTrade);
  return collectDeployedTrace({ local: input.local, client: input.client, transactionHash: input.transactionHash,
    expected: structuredClone(input.expected) }, CHAIN_ID, nativeTrade);
}

export async function collectSelectedNativeSmaBDeployedUserOpValidationTrace(input: SmaBDeployedUserOpValidationTraceInput & {
  nativeTrade: Parameters<typeof decodeAndValidateNativeV2Trade>[0];
}) {
  return collectDeployedTrace({ local: input.local, client: input.client, transactionHash: input.transactionHash,
    expected: structuredClone(input.expected) }, SELECTED_ACCOUNT_CHAIN_ID, structuredClone(input.nativeTrade));
}

/** Explicit native branch: account mode comes from bound handleOps initCode,
 * with first deployment, original owner and CREATE2 observed in this transaction. */
export async function collectNativeSmaBUserOpValidationTrace(input: SmaBDeployedUserOpValidationTraceInput & {
  nativeTrade: Parameters<typeof decodeAndValidateNativeV2Trade>[0];
}) {
  return collectNativeTraceForChain(input, CHAIN_ID);
}

export function collectSelectedNativeSmaBUserOpValidationTrace(input: SmaBDeployedUserOpValidationTraceInput & {
  nativeTrade: Parameters<typeof decodeAndValidateNativeV2Trade>[0];
}) {
  return collectNativeTraceForChain(input, SELECTED_ACCOUNT_CHAIN_ID);
}

async function collectNativeTraceForChain<ChainId extends 4663 | 46630>(input: SmaBDeployedUserOpValidationTraceInput & {
  nativeTrade: Parameters<typeof decodeAndValidateNativeV2Trade>[0];
}, expectedChainId: ChainId) {
  const result = await collectDeployedTrace({ local: input.local, client: input.client, transactionHash: input.transactionHash,
    expected: structuredClone(input.expected) }, expectedChainId, structuredClone(input.nativeTrade), true);
  if (!result.deployment) return result;
  return Object.freeze({ ...result, evidence: Object.freeze({ ...result.evidence,
    scope: 'local-sma-b-v2.0.0-first-deployment-fallback-eoa-validation-trace' as const,
    validation: { ...result.evidence.validation, kind: 'sma-b-v2.0.0-counterfactual-fallback-eoa-observed' as const,
      factory: { address: result.deployment.factory, inputHash: result.deployment.inputHash,
        create2InitCodeHash: result.deployment.create2InitCodeHash, deploymentLogIndex: result.deployment.deploymentLogIndex } },
    limitations: result.evidence.limitations.filter(item => !item.startsWith('Factory identity') && !item.startsWith('Only one'))
      .concat('Only one direct EntryPoint v0.7 native operation with observed same-transaction SMA-B creation is supported.') }) });
}

function nativeDeploymentEvent(raw: unknown, operation: ObservedOperation, input: SmaBDeployedUserOpValidationTraceExpected) {
  const receipt = plain(raw, 'native first receipt');
  const logs = Array.isArray(receipt.logs) ? receipt.logs.map(log => plain(log, 'native first log')) : fail('native first logs required');
  const abi = parseAbi(['event AccountDeployed(bytes32 indexed userOpHash,address indexed sender,address factory,address paymaster)']);
  const topic = encodeEventTopics({ abi, eventName: 'AccountDeployed' })[0];
  const matches = logs.filter(log => isAddressEqual(address(log.address, 'deployment emitter'), ENTRY_POINT)
    && Array.isArray(log.topics) && log.topics[0] === topic);
  const log = matches[0];
  if (matches.length !== 1 || !log || !Array.isArray(log.topics)) fail('exact native AccountDeployed event required');
  const topics = log.topics.map(value => hash(value, 'deployment topic')) as [Hex, ...Hex[]];
  const event = decodeEventLog({ abi, eventName: 'AccountDeployed', topics, data: hex(log.data, 'deployment data'), strict: true });
  const expectedTopics = encodeEventTopics({ abi, eventName: 'AccountDeployed', args: { userOpHash: operation.userOperationHash, sender: input.vault } });
  if (JSON.stringify(topics) !== JSON.stringify(expectedTopics)
    || hex(log.data, 'deployment data') !== encodeAbiParameters([{ type: 'address' }, { type: 'address' }], [input.factory, getAddress('0x0000000000000000000000000000000000000000')])
    || !isAddressEqual(event.args.factory, input.factory)) fail('native AccountDeployed binding mismatch');
  const marker = encodeEventTopics({ abi: entryPoint07Abi, eventName: 'BeforeExecution' })[0];
  const boundary = logs.findIndex(value => isAddressEqual(address(value.address, 'marker emitter'), ENTRY_POINT)
    && Array.isArray(value.topics) && value.topics[0] === marker);
  if (boundary < 0 || logs.indexOf(log) >= boundary) fail('native deployment must precede execution');
  return index(log.logIndex, 'deployment log index');
}

function nativeCreation(tree: ParsedCallTree, operation: ObservedOperation, input: SmaBDeployedUserOpValidationTraceExpected) {
  if (!isAddressEqual(input.owner, input.originalOwner)) fail('first operation owner differs from immutable original owner');
  const factories = tree.nodes.filter(frame => frame.type === 'CALL' && isAddressEqual(frame.to, input.factory));
  const factory = factories[0], creator = factory?.parent === null || factory?.parent === undefined ? undefined : tree.nodes[factory.parent];
  const initCode = expectedInitCode(input);
  const creations = tree.nodes.filter(frame => frame.type === 'CREATE2');
  const creation = creations[0];
  if (factories.length !== 1 || !factory || factory.input !== operation.factoryData || factory.error !== null || factory.value !== '0'
    || !creator || creator.type !== 'CALL' || creator.parent !== tree.rootIndex || !isAddressEqual(creator.from, ENTRY_POINT)
    || !isAddressEqual(creator.to, '0xEFC2c1444eBCC4Db75e7613d20C6a62fF67A167C')
    || !isAddressEqual(factory.from, creator.to) || creator.error !== null || creator.value !== '0'
    || creations.length !== 1 || !creation || creation.parent !== factory.index || creation.error !== null
    || creation.value !== '0' || !isAddressEqual(creation.from, input.factory) || !isAddressEqual(creation.to, input.vault)
    || creation.input !== initCode || !isAddressEqual(expectedVaultAddress(input, initCode), input.vault)
    || creation.children.length !== 0) fail('bound native first-operation factory CREATE2 required');
  return { creation, inputHash: keccak256(factory.input), create2InitCodeHash: keccak256(initCode) };
}

async function collectDeployedTrace<ChainId extends 4663 | 46630>(
  { local, client, transactionHash, expected }: SmaBDeployedUserOpValidationTraceInput, expectedChainId: ChainId,
  nativeTrade?: Parameters<typeof decodeAndValidateNativeV2Trade>[0], allowFirst = false) {
  if (typeof local !== 'function') fail('local RPC function required');
  if (!client || typeof client !== 'object') fail('public client required');
  const sourceHash = hash(transactionHash, 'transaction hash');
  const input = deployedExpectedInput(expected);
  const before = await snapshot(client, sourceHash, nativeTrade !== undefined);
  if (chainId(await client.getChainId(), 'client chainId') !== BigInt(expectedChainId) || chainId(await local('eth_chainId', [], 20_000), 'local chainId') !== BigInt(expectedChainId)) fail('local/client chain mismatch');
  const identity = sourceIdentity(before.transaction, before.receipt, before.block, sourceHash);
  const handle = decodeFunctionData({ abi: entryPoint07Abi, data: hex(plain(before.transaction, 'transaction').input, 'transaction input') });
  const first = allowFirst && handle.functionName === 'handleOps' && handle.args[0].length === 1 && handle.args[0][0]!.initCode !== '0x';
  const mode = first ? 'counterfactual' : 'deployed';
  const operation = terminalOperation(before.receipt, before.transaction, input, mode, nativeTrade !== undefined, expectedChainId);
  if (!await parsedAsync('ff00 signature', () => verifyMessage({ address: input.owner, message: { raw: operation.userOperationHash },
    signature: `0x${operation.packed.signature.slice(6)}` as Hex }))) fail('ff00 signature does not recover expected owner');
  const prestateRaw = await localTrace(local, { tracer: 'prestateTracer', timeout: '5s' }, sourceHash);
  const diffRaw = await localTrace(local, { tracer: 'prestateTracer', tracerConfig: { diffMode: true }, timeout: '5s' }, sourceHash);
  const callRaw = await localTrace(local, { tracer: 'callTracer', tracerConfig: { withLog: true }, timeout: '5s' }, sourceHash);
  const traceCommitments = { fullPrestate: commitment(prestateRaw, 'full prestate trace'), diff: commitment(diffRaw, 'diff trace'), call: commitment(callRaw, 'call trace') };
  const prestate = addressMap(prestateRaw, 'full prestate');
  const firstDeployment = first ? (() => {
    prestateCode(prestate, ENTRY_POINT, ENTRY_POINT_CODE_HASH, 'EntryPoint');
    prestateCode(prestate, input.factory, FACTORY_CODE_HASH, 'factory');
    prestateCode(prestate, input.implementation, IMPLEMENTATION_CODE_HASH, 'SMA-B implementation');
    emptyCounterfactual(prestate.get(input.vault.toLowerCase()), 'full prestate vault');
    const diff = plain(diffRaw, 'diff trace');
    emptyCounterfactual(addressMap(diff.pre, 'diff pre').get(input.vault.toLowerCase()), 'diff prestate vault');
    return postDeployment(addressMap(diff.post, 'diff post'), input.vault, input.originalOwner);
  })() : null;
  const deployed: ReturnType<typeof deployedPrestate> = firstDeployment ? { runtimeHash: firstDeployment.runtimeHash,
    ownerResolution: { originalOwner: input.originalOwner, storedFallbackSigner: getAddress('0x0000000000000000000000000000000000000000'),
      fallbackSignerDisabled: false as const, source: 'appended-original-owner' as const, packedStorageSlot: FALLBACK_SIGNER_SLOT,
      packedStorageValue: `0x${'0'.repeat(64)}` as Hex } } : deployedPrestate(prestate, input);
  unchangedDeployedState(diffRaw, prestate, input, first);
  const tree = parseCallTree(callRaw, plain(before.transaction, 'transaction'));
  const creation = first ? { ...nativeCreation(tree, operation, input), deploymentLogIndex: nativeDeploymentEvent(before.receipt, operation, input) } : null;
  const historicalNative = nativeTrade === undefined ? undefined : { ...nativeTrade,
    policy: { ...nativeTrade.policy, nowUnixSeconds: uint(plain(before.block, 'block').timestamp, 'block timestamp') } };
  // Preserve standalone legacy Node strip-only loading. Native dependencies are
  // resolved only on this separately selected native observation branch.
  const native = historicalNative === undefined ? undefined : { trade: historicalNative,
    decoded: operation.success ? (await import('./nativeRouterTradeDecoder.js')).decodeAndValidateNativeV2Trade(historicalNative) : undefined };
  const trace = deployedTraceSemantics(tree, operation, input, native, creation?.creation);
  const after = await snapshot(client, sourceHash, nativeTrade !== undefined);
  const rechecked = sourceIdentity(after.transaction, after.receipt, after.block, sourceHash);
  const afterOperation = terminalOperation(after.receipt, after.transaction, input, mode, nativeTrade !== undefined, expectedChainId);
  if (JSON.stringify(identity) !== JSON.stringify(rechecked) || afterOperation.userOperationHash !== operation.userOperationHash
    || afterOperation.success !== operation.success
    || chainId(await client.getChainId(), 'client chainId') !== BigInt(expectedChainId) || chainId(await local('eth_chainId', [], 20_000), 'local chainId') !== BigInt(expectedChainId)) fail('source changed during trace collection');
  const evidence: SmaBDeployedUserOpValidationTraceEvidence<ChainId> = { schemaVersion: 1, scope: 'local-sma-b-v2.0.0-deployed-fallback-eoa-validation-trace', chainId: expectedChainId,
    source: { ...identity, userOperationHash: operation.userOperationHash, nonce: operation.packed.nonce.toString(), traceCommitments,
      traceProfile: { prestate: 'prestateTracer', diff: 'prestateTracer.diffMode', call: 'callTracer.withLog' }, callTrace: tree.redacted, traceNotCryptographicProof: true },
    validation: { kind: 'sma-b-v2.0.0-deployed-fallback-eoa-observed', vault: input.vault, owner: input.owner,
      nonceLane: input.nonceLane.toString(), lookupKey: '0', globalValidation: true, deferredAction: false, directCallValidation: false,
      signatureFormat: 'ff00-eoa-65-byte-redacted', signatureCommitment: operation.signatureCommitment,
      factory: { address: input.factory, create2InitCodeHash: keccak256(expectedInitCode({ ...input, owner: input.originalOwner })), provenanceOnly: true },
      implementation: { address: input.implementation, runtimeHash: deployed.runtimeHash, implementationSlot: IMPLEMENTATION_SLOT },
      ownerResolution: deployed.ownerResolution, ecrecover: trace.ecrecover,
      validateUserOp: { inputHash: trace.validationInputHash, outputHash: trace.validationOutputHash, validationData: '0' }, executionInputHash: trace.executionInputHash },
    traceBounds: { maxBytesPerTrace: MAX_TRACE_BYTES, maxAccounts: MAX_ACCOUNTS, maxStorageSlotsPerAccount: MAX_STORAGE_SLOTS,
      maxFrames: MAX_TRACE_FRAMES, maxDepth: MAX_DEPTH, observedFrames: tree.frames },
    limitations: ['Only one direct EntryPoint v0.7 handleOps deployed SMA-B v2.0.0 fallback EOA operation with native execute/executeBatch is observed.',
      'Trusted local debug traces are not cryptographic proofs and do not establish remote-provider finality.',
      'Accessed prestate slots are not a complete module inventory; unrelated installed validators/modules are not asserted absent or approved.',
      'Selected validation hooks, execution module routes, reentry, authority/storage changes, deferred/direct validation, ERC-1271 owners, paymasters, aggregation and multi-operation bundles are unsupported.',
      'Factory identity is immutable CREATE2 provenance only; this transaction does not prove deployment or factory execution.',
      'No persistence, settlement, movements, fee, P&L, referral, points, signing, submission, or publication authority.'],
    ...falseAuthorities };
  return Object.freeze({ evidence: Object.freeze(evidence), prefundPaid: trace.prefundPaid,
    accountDeployment: first ? 'created-in-selected-transaction' as const : 'already-deployed' as const,
    deployment: creation ? { factory: input.factory, inputHash: creation.inputHash, create2InitCodeHash: creation.create2InitCodeHash,
      runtimeHash: deployed.runtimeHash, originalOwner: input.originalOwner, deploymentLogIndex: creation.deploymentLogIndex } : null, success: operation.success,
    nativeReceipts: trace.nativeReceipts, entryPointDepositReconciliationVerified: false as const,
    vaultGasDebitConserved: false as const, ...falseAuthorities });
}
